import { LinkError } from '@apollo/client/errors'
import { Component, Inject, ChangeDetectionStrategy } from '@angular/core'
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import {
  ImportTrygroupSalesPerformanceResult,
  TrygroupDashboardSettingsService,
  TrygroupMonthlySalesPerformanceImportWarning,
} from '../../../../services/trygroup-dashboard-settings/trygroup-dashboard-settings.service'

const WARNING_DISPLAY_LIMIT = 100
const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
// UI 側で先に弾いて、署名付き URL 発行後のアップロード失敗より
// 分かりやすいメッセージを出す。
const MAX_UPLOAD_BYTES = 10_000_000

@UntilDestroy()
@Component({
  selector: 'app-trygroup-monthly-sales-performance-import-settings-page',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule],
  template: `
    <section class="settings-panel">
      <div class="panel-header">
        <div>
          <h3>面入率取込</h3>
          <p>A1 面入率実績 Excel を取り込みます。</p>
        </div>
      </div>

      <div
        class="drop-zone"
        [class.drop-zone--active]="isDragging"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
      >
        <p class="drop-zone__guidance">ここにファイルをドラッグ&ドロップ</p>
        <div class="import-controls">
          <input
            #fileInput
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            (change)="onFileSelected($event)"
          />
          <button
            mat-stroked-button
            type="button"
            [disabled]="isImporting"
            (click)="fileInput.click()"
          >
            <mat-icon>attach_file</mat-icon>
            ファイル選択
          </button>
          <span class="file-name">{{ selectedFile?.name || '未選択' }}</span>
          <button
            mat-flat-button
            color="primary"
            type="button"
            [disabled]="!selectedFile || isImporting"
            (click)="executeImport()"
          >
            <mat-icon>upload_file</mat-icon>
            {{ isImporting ? 'インポート中...' : 'インポート実行' }}
          </button>
        </div>
      </div>

      @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .settings-panel {
        padding: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
      }

      .panel-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 18px;
      }

      h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
      }

      p {
        margin: 6px 0 0;
        color: #64748b;
        font-size: 13px;
      }

      .import-controls {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        align-items: center;
      }

      .drop-zone {
        padding: 18px;
        border: 2px dashed #cbd5e1;
        border-radius: 8px;
        background: #f8fafc;
        transition:
          background-color 120ms ease,
          border-color 120ms ease;
      }

      .drop-zone--active {
        border-color: #2563eb;
        background: #eff6ff;
      }

      .drop-zone__guidance {
        margin: 0 0 12px;
        color: #475569;
        font-weight: 700;
      }

      button mat-icon {
        margin-right: 6px;
      }

      .file-name {
        min-width: 220px;
        max-width: min(520px, 100%);
        overflow: hidden;
        color: #334155;
        font-size: 13px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .error {
        margin-top: 16px;
        color: #dc2626;
        font-weight: 700;
      }
    `,
  ],
})
export class TrygroupMonthlySalesPerformanceImportSettingsPageComponent {
  selectedFile: File | null = null
  isImporting = false
  isDragging = false
  errorMessage = ''

  constructor(
    private trygroupDashboardSettingsService: TrygroupDashboardSettingsService,
    private dialog: MatDialog,
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.item(0) ?? null
    const isAccepted = this.selectFile(file)
    if (!isAccepted) {
      input.value = ''
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.isImporting ? 'none' : 'copy'
    }

    if (this.isImporting) {
      this.isDragging = false
      return
    }

    this.isDragging = true
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false
  }

  onDrop(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDragging = false

    if (this.isImporting) {
      return
    }

    const file = event.dataTransfer?.files.item(0) ?? null
    this.selectFile(file)
  }

  executeImport(): void {
    if (!this.selectedFile || this.isImporting) {
      return
    }

    this.isImporting = true
    this.errorMessage = ''
    this.trygroupDashboardSettingsService
      .importMonthlySalesPerformance(this.selectedFile)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.isImporting = false
          this.dialog.open<
            TrygroupMonthlySalesPerformanceImportResultDialogComponent,
            ImportTrygroupSalesPerformanceResult
          >(TrygroupMonthlySalesPerformanceImportResultDialogComponent, {
            width: '760px',
            maxWidth: 'calc(100vw - 32px)',
            data: result,
          })
        },
        error: (err: unknown) => {
          this.isImporting = false
          // backend の ValidationError は throwIfCustomError 経由で
          // Error(message) として伝播し、行番号付きの具体的メッセージを含む。
          // message が無いケースと、リンク由来（通信断・非 2xx 応答）で英語の技術的な
          // 文言しか持たないケースは generic 文言にする。
          this.errorMessage =
            err instanceof Error && !LinkError.is(err) && err.message
              ? err.message
              : '面入率実績 Excel の取込に失敗しました。'
        },
      })
  }

  private selectFile(file: File | null): boolean {
    this.errorMessage = ''

    if (!file) {
      this.selectedFile = null
      return true
    }

    // input の accept=".xlsx" はブラウザのヒントに過ぎず、ユーザが
    // 「すべてのファイル」に切り替えれば xlsx 以外も選択できる。drop と
    // 同じく JS 側で拡張子 / MIME を強制し、UX/防御を二重化する。
    if (!this.isXlsxFile(file)) {
      this.selectedFile = null
      this.errorMessage = '.xlsx ファイルのみ取り込めます。'
      return false
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      this.selectedFile = null
      const limitMb = Math.floor(MAX_UPLOAD_BYTES / 1_000_000)
      this.errorMessage = `ファイルサイズが上限 ${limitMb}MB を超えています。`
      return false
    }

    this.selectedFile = file
    return true
  }

  private isXlsxFile(file: File): boolean {
    return (
      file.name.toLowerCase().endsWith('.xlsx') || file.type === XLSX_MIME_TYPE
    )
  }
}

@Component({
  selector: 'app-trygroup-monthly-sales-performance-import-result-dialog',
  standalone: true,
  imports: [MatButtonModule, MatDialogModule, MatIconModule],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>task_alt</mat-icon>
      インポート結果
    </h2>

    <mat-dialog-content>
      <div class="summary-grid">
        <div>
          <span class="label">対象月</span>
          <strong>{{ formatMonths(data.months) }}</strong>
        </div>
        <div>
          <span class="label">処理行数</span>
          <strong>{{ data.totalProcessedRows }}</strong>
        </div>
        <div>
          <span class="label">取込件数</span>
          <strong>{{ data.totalImportedRecords }}</strong>
        </div>
      </div>

      <table class="month-table">
        <thead>
          <tr>
            <th>対象月</th>
            <th>処理行数</th>
            <th>取込件数</th>
          </tr>
        </thead>
        <tbody>
          @for (month of data.perMonth; track month.yyyymm) {
            <tr>
              <td>{{ formatMonth(month.yyyymm) }}</td>
              <td>{{ month.processedRows }}</td>
              <td>{{ month.importedRecords }}</td>
            </tr>
          }
        </tbody>
      </table>

      <section class="warning-section">
        <h3>警告 {{ data.warnings.length }} 件</h3>
        @if (data.warnings.length === 0) {
          <p class="status">警告はありません。</p>
        } @else {
          <div class="warning-scroll">
            @for (warning of visibleWarnings; track $index) {
              <div class="warning-item">
                <strong class="warning-code">{{ warning.code }}</strong>
                <span class="warning-message">{{
                  formatWarning(warning)
                }}</span>
              </div>
            }
            @if (hiddenWarningCount > 0) {
              <div class="warning-more">他 {{ hiddenWarningCount }} 件</div>
            }
          </div>
        }
      </section>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-flat-button color="primary" type="button" (click)="close()">
        OK
      </button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      h2 {
        display: flex;
        gap: 8px;
        align-items: center;
        margin: 0;
      }

      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        margin-bottom: 18px;
      }

      .summary-grid > div {
        padding: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
      }

      .label {
        display: block;
        margin-bottom: 4px;
        color: #64748b;
        font-size: 12px;
      }

      .summary-grid strong {
        color: #0f172a;
        font-size: 16px;
      }

      .month-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }

      th,
      td {
        padding: 10px 8px;
        border-bottom: 1px solid #e2e8f0;
        text-align: left;
      }

      th {
        color: #475569;
        font-weight: 800;
      }

      .warning-section {
        margin-top: 18px;
      }

      h3 {
        margin: 0 0 8px;
        font-size: 15px;
        font-weight: 800;
      }

      .status {
        margin: 0;
        color: #64748b;
        font-size: 13px;
      }

      .warning-scroll {
        max-height: min(46vh, 420px);
        overflow: auto;
        border: 1px solid #fde68a;
        border-radius: 8px;
        background: #fffbeb;
      }

      .warning-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
        align-items: flex-start;
        padding: 10px 12px;
        border-bottom: 1px solid #fde68a;
        color: #92400e;
        font-size: 12px;
      }

      .warning-code {
        box-sizing: border-box;
        max-width: 100%;
        padding: 2px 6px;
        border-radius: 4px;
        background: #fef3c7;
        color: #92400e;
        font-size: 11px;
        line-height: 1.4;
        letter-spacing: 0;
        overflow-wrap: anywhere;
        white-space: normal;
        word-break: break-word;
      }

      .warning-message {
        display: block;
        max-width: 100%;
        color: #92400e;
        line-height: 1.6;
        letter-spacing: 0;
        overflow-wrap: anywhere;
        white-space: normal;
        word-break: break-word;
      }

      .warning-more {
        display: block;
        padding: 10px 12px;
        border-bottom: 0;
        color: #92400e;
        font-size: 12px;
        font-weight: 800;
        text-align: center;
      }

      @media (max-width: 640px) {
        .summary-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class TrygroupMonthlySalesPerformanceImportResultDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: ImportTrygroupSalesPerformanceResult,
    private dialogRef: MatDialogRef<TrygroupMonthlySalesPerformanceImportResultDialogComponent>,
  ) {}

  get visibleWarnings(): TrygroupMonthlySalesPerformanceImportWarning[] {
    return this.data.warnings.slice(0, WARNING_DISPLAY_LIMIT)
  }

  get hiddenWarningCount(): number {
    return Math.max(0, this.data.warnings.length - WARNING_DISPLAY_LIMIT)
  }

  formatMonths(months: string[]): string {
    return months.map((month) => this.formatMonth(month)).join(', ')
  }

  formatMonth(yyyymm: string): string {
    if (!/^\d{6}$/.test(yyyymm)) {
      return yyyymm
    }

    return `${yyyymm.slice(0, 4)}/${yyyymm.slice(4, 6)}`
  }

  formatWarning(warning: TrygroupMonthlySalesPerformanceImportWarning): string {
    const details = [
      warning.rowNumber ? `${warning.rowNumber}行目` : null,
      warning.userEmail,
      warning.yyyymm ? this.formatMonth(warning.yyyymm) : null,
      warning.columnName ? `${warning.columnName}列` : null,
    ].filter((value): value is string => Boolean(value))

    if (details.length === 0) {
      return warning.message
    }

    return `${warning.message} (${details.join(' / ')})`
  }

  close(): void {
    this.dialogRef.close()
  }
}
